import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { SearchBar } from './home/search-bar';
import { ContentGrid } from './home/content-grid';
import { QuoteCard } from './home/quote-card';
import { KeywordFilters } from './home/keyword-filters';
import { useProfile } from '@/lib/providers';
import Loading from './loading';
import { supabase } from '@/lib/supabase';
import { AdminRole, Content, ContentConfig, ContentWithConfig } from '@/types/custom';
import AudioCard from './audio-card';
import { canSeeBinauralBeats, getDeepLink } from '@/lib/constants';
import { useTeamMeditations } from '@/lib/hooks/use-team-meditations';
import { useSamenMeditations } from '@/lib/hooks/use-samen-meditations';
import { useAdminPreview } from '@/lib/admin-preview-context';
import { Users, CalendarPlus } from 'lucide-react';
import { downloadIcs } from '@/lib/utils';

function Home() {
  const navigate = useNavigate();
  const { profile, loadingProfile } = useProfile();
  const { effectiveRole } = useAdminPreview();
  const [featuredContent, setFeaturedContent] = useState<ContentWithConfig[]>([]);
  // Binaural beats are unreleased; a featured one would slip past the catalogue
  // filter, since this row is fetched straight from the table.
  const visibleFeaturedContent = canSeeBinauralBeats(profile?.role === AdminRole)
    ? featuredContent
    : featuredContent.filter((item) => item.content.content_type !== 'binaural');
  const [isLoadingFeatured, setIsLoadingFeatured] = useState(true);

  const isHnH = effectiveRole === 'hoofdhartuser';
  const team = profile?.teams.at(profile.active_team_idx);
  const { nextMeditation: teamNext, activeMeditation: teamActive, activeMeditations: teamActives } = useTeamMeditations(isHnH ? undefined : team?.id);
  const { nextMeditation: samenNext, activeMeditation: samenActive, activeMeditations: samenActives } = useSamenMeditations(isHnH ? profile?.samen?.id : undefined);
  const nextMeditation = isHnH ? samenNext : teamNext;
  const activeMeditation = isHnH ? samenActive : teamActive;
  const activeMeditations = isHnH ? samenActives : teamActives;
  const meditationLink = isHnH ? '/samen?tab=samen-meditatie' : '/team?tab=team-meditatie';
  const [countdown, setCountdown] = useState<string | null>(null);

  const upcomingMeditation = nextMeditation ?? activeMeditation;

  // Countdown timer (activates 30 min before)
  useEffect(() => {
    if (!upcomingMeditation) { setCountdown(null); return; }

    const tick = () => {
      const start = new Date(upcomingMeditation.scheduled_at).getTime();
      const now = Date.now();
      const diff = start - now;

      if (diff > 30 * 60 * 1000) { setCountdown(null); return; }
      if (diff <= 0) { setCountdown('Nu bezig!'); return; }

      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setCountdown(`${mins}:${secs.toString().padStart(2, '0')}`);
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [upcomingMeditation?.id]);

  useEffect(() => {
    if (!profile) return;

    const fetchFeatured = async () => {
      setIsLoadingFeatured(true);
      try {
        // Build scope filters: global + user's sector, org, team(s), and user ID
        const scopes: { scope_type: string; scope_id: string | null }[] = [
          { scope_type: 'global', scope_id: null },
          { scope_type: 'user', scope_id: profile.id },
        ];

        for (const team of profile.teams ?? []) {
          scopes.push({ scope_type: 'team', scope_id: team.id });
          scopes.push({ scope_type: 'organization', scope_id: team.organization.id });
          scopes.push({ scope_type: 'sector', scope_id: team.organization.sector.id });
        }

        // Query: get all featured items matching any of the user's scopes
        // We use an OR filter for all scope combinations
        const orFilters = scopes
          .map((s) =>
            s.scope_id === null
              ? `and(scope_type.eq.${s.scope_type},scope_id.is.null)`
              : `and(scope_type.eq.${s.scope_type},scope_id.eq.${s.scope_id})`,
          )
          .join(',');

        const { data, error } = await supabase
          .from('homepage_featured_content')
          .select('*')
          .or(orFilters)
          .order('sort_order');

        if (error) throw error;
        if (!data || data.length === 0) {
          setFeaturedContent([]);
          return;
        }

        // Fetch content details
        const contentIds = [...new Set(data.map((f) => f.content_id))];
        const { data: contentData, error: contentError } = await supabase
          .from('content')
          .select('*, content_configs(*), keywords(*)')
          .in('id', contentIds)
          .eq('published', true);

        if (contentError) throw contentError;

        const mapped: ContentWithConfig[] = (contentData ?? []).map((item) => ({
          content: {
            id: item.id,
            title: item.title,
            description: item.description,
            body: item.body,
            image_url: item.image_url,
            content_type: item.content_type,
            published: item.published,
            created_at: item.created_at,
            updated_at: item.updated_at,
          } as Content,
          configs: (item.content_configs || []) as ContentConfig[],
          keywords: item.keywords ?? [],
        }));

        // Sort by featured sort_order
        const orderMap = new Map(data.map((f) => [f.content_id, f.sort_order]));
        mapped.sort((a, b) => (orderMap.get(a.content.id) ?? 0) - (orderMap.get(b.content.id) ?? 0));

        setFeaturedContent(mapped);
      } catch (error) {
        console.error('Error fetching featured content:', error);
      } finally {
        setIsLoadingFeatured(false);
      }
    };

    fetchFeatured();
  }, [profile]);

  // Fetch a random quote from the database (scoped by role)
  const [activeQuote, setActiveQuote] = useState<{ quote: string; author: string; imageUrl: string } | null>(null);

  useEffect(() => {
    const fetchQuote = async () => {
      let query = supabase
        .from('quotes')
        .select('quote, author, image_url');

      if (effectiveRole === 'hoofdhartuser') {
        query = query.eq('role_scope', 'hoofdhartuser');
      } else {
        query = query.is('role_scope', null);
      }

      const { data } = await query;

      if (data && data.length > 0) {
        const random = data[Math.floor(Math.random() * data.length)];
        setActiveQuote({
          quote: random.quote,
          author: random.author,
          imageUrl: random.image_url ?? 'https://images.unsplash.com/photo-1519834785169-98be25ec3f84?q=80&w=1200',
        });
      }
    };
    fetchQuote();
  }, [effectiveRole]);

  if (loadingProfile) return <Loading />;

  return (
    <div className="min-h-screen p-8 space-y-8">
      <section>
        <div className="mb-8">
          <h2 className="mb-4">
            <span className="text-2xl">
              Welkom <span className="font-bold">{profile?.naam?.split(' ')[0]}</span>, wat heb je nu nodig?
            </span>
          </h2>
          <div className="mb-4">
            <SearchBar />
          </div>
          <KeywordFilters />
        </div>

        {/* Featured Content */}
        {!isLoadingFeatured && visibleFeaturedContent.length > 0 && (
          <>
            <h2 className="text-xl text-muted-foreground font-bold mb-4">Uitgelicht</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
              {visibleFeaturedContent.map((item) => (
                <AudioCard
                  key={item.content.id}
                  content={item.content}
                  showLabel
                  onClick={() =>
                    navigate(getDeepLink(item.content.content_type, item.content.id))
                  }
                />
              ))}
            </div>
          </>
        )}

        {/* Active meditation banners */}
        {activeMeditations.filter((am) => am.id !== upcomingMeditation?.id).map((am) => (
          <Link
            key={am.id}
            to={meditationLink}
            className={`block w-full p-4 rounded-xl bg-gradient-to-r ${isHnH ? 'from-red-500/20 to-rose-500/20 border-red-500/30 hover:border-red-500/50' : 'from-green-500/20 to-emerald-500/20 border-green-500/30 hover:border-green-500/50'} border transition-colors mb-3`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Users className={`h-5 w-5 ${isHnH ? 'text-red-500' : 'text-green-500'}`} />
                  <span className={`absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full ${isHnH ? 'bg-red-500' : 'bg-green-500'} animate-pulse`} />
                </div>
                <div>
                  <p className="font-medium text-sm">
                    {isHnH ? 'Samen Activiteit' : 'Team Activiteit'} — Nu bezig!
                  </p>
                  <p className="text-xs text-muted-foreground">{am.meditation_title}</p>
                </div>
              </div>
              <p className={`text-lg font-bold ${isHnH ? 'text-red-500' : 'text-green-500'}`}>Nu bezig!</p>
            </div>
          </Link>
        ))}

        {/* Team/Samen upcoming/active meditation banner */}
        {upcomingMeditation && (
          <Link
            to={meditationLink}
            className={`block w-full p-4 rounded-xl bg-gradient-to-r ${isHnH ? 'from-red-500/20 to-rose-500/20 border-red-500/30 hover:border-red-500/50' : 'from-green-500/20 to-emerald-500/20 border-green-500/30 hover:border-green-500/50'} border transition-colors mb-6`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Users className={`h-5 w-5 ${isHnH ? 'text-red-500' : 'text-green-500'}`} />
                  <span className={`absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full ${isHnH ? 'bg-red-500' : 'bg-green-500'} ${countdown === 'Nu bezig!' ? 'animate-pulse' : ''}`} />
                </div>
                <div>
                  <p className="font-medium text-sm">{(() => {
                    const isExercise = (upcomingMeditation as any).activity_type === 'exercise';
                    if (isHnH) return isExercise ? 'Volgende Samen Oefening' : 'Volgende Samen Meditatie';
                    return isExercise ? 'Volgende Team Oefening' : 'Volgende Team Meditatie';
                  })()}</p>
                  <p className="text-xs text-muted-foreground">
                    {upcomingMeditation.meditation_title}
                    {' — '}
                    {new Date(upcomingMeditation.scheduled_at).toLocaleDateString('nl-NL', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                    })}
                    {' om '}
                    {new Date(upcomingMeditation.scheduled_at).toLocaleTimeString('nl-NL', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {countdown && (
                  <div className="text-right">
                    <p className={`text-lg font-bold ${isHnH ? 'text-red-500' : 'text-green-500'} tabular-nums`}>
                      {countdown === 'Nu bezig!' ? countdown : `over ${countdown}`}
                    </p>
                  </div>
                )}
                {(() => {
                  const scheduledDate = new Date(upcomingMeditation.scheduled_at);
                  const today = new Date();
                  const isSameDay =
                    scheduledDate.getDate() === today.getDate() &&
                    scheduledDate.getMonth() === today.getMonth() &&
                    scheduledDate.getFullYear() === today.getFullYear();
                  if (isSameDay) return null;
                  return (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        downloadIcs(
                          upcomingMeditation.meditation_title,
                          upcomingMeditation.scheduled_at,
                          upcomingMeditation.duration_minutes ?? 20,
                          { variant: isHnH ? 'samen' : 'team', activityType: (upcomingMeditation as any).activity_type },
                        );
                      }}
                      className={`p-1.5 rounded-md ${isHnH ? 'hover:bg-red-500/20 text-red-500' : 'hover:bg-green-500/20 text-green-500'}`}
                      title="Voeg toe aan agenda"
                    >
                      <CalendarPlus className="h-5 w-5" />
                    </button>
                  );
                })()}
              </div>
            </div>
          </Link>
        )}

        <h2 className="text-xl text-muted-foreground font-bold mb-4">Nieuwste Audio</h2>
        <ContentGrid articlesOnly={false} />

        <br />
        <h2 className="text-xl text-muted-foreground font-bold mb-4">Nieuwste Artikelen</h2>
        <ContentGrid articlesOnly={true} />
      </section>

      {activeQuote && (
        <section>
          <h2 className="text-xl text-muted-foreground font-bold mb-4">Quote van de Dag</h2>
          <QuoteCard
            quote={activeQuote.quote}
            author={activeQuote.author}
            imageUrl={activeQuote.imageUrl}
          />
        </section>
      )}
    </div>
  );
}

export default Home;
